import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FiPhone, FiMessageSquare, FiX } from 'react-icons/fi';

const ConnectIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
    <path d="M14.05 2a9 9 0 018 7.94" opacity="0.6"/>
    <path d="M14.05 6A5 5 0 0118 10" opacity="0.6"/>
  </svg>
);

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const FloatingButtons = ({ onOpenChat }) => {
  const [expanded, setExpanded] = useState(false);
  const hasMobileBar = useLocation().pathname.startsWith('/villas/');

  return (
    <div className={`fixed right-3 md:right-6 z-50 flex flex-col items-end gap-3 ${hasMobileBar ? 'bottom-[88px] lg:bottom-6' : 'bottom-4 md:bottom-6'}`} data-testid="floating-buttons">
      {/* Expanded options */}
      {expanded && (
        <div className="flex flex-col items-end gap-2 mb-1 animate-in fade-in slide-in-from-bottom-2 duration-200">
          {/* Call */}
          <a
            href="tel:+919820088292"
            data-testid="floating-call-btn"
            className="flex items-center gap-3 bg-white rounded-full pl-4 pr-2 py-2 shadow-lg border border-gray-100 hover:shadow-xl transition-all group"
          >
            <span className="text-[#1A1A2E] text-xs font-semibold whitespace-nowrap">Talk to us: 98200 88292</span>
            <div className="w-10 h-10 rounded-full bg-green-600 text-white flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
              <FiPhone className="w-4 h-4" />
            </div>
          </a>

          {/* WhatsApp */}
          <a
            href="https://wa.me/919820088292"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="floating-whatsapp-btn"
            className="flex items-center gap-3 bg-white rounded-full pl-4 pr-2 py-2 shadow-lg border border-gray-100 hover:shadow-xl transition-all group"
          >
            <span className="text-[#1A1A2E] text-xs font-semibold whitespace-nowrap">WhatsApp Chat</span>
            <div className="w-10 h-10 rounded-full bg-[#25D366] text-white flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
              <WhatsAppIcon />
            </div>
          </a>

          {/* AI Chat */}
          <button
            onClick={() => { onOpenChat(); setExpanded(false); }}
            data-testid="floating-ai-btn"
            className="flex items-center gap-3 bg-white rounded-full pl-4 pr-2 py-2 shadow-lg border border-gray-100 hover:shadow-xl transition-all group"
          >
            <span className="text-[#1A1A2E] text-xs font-semibold whitespace-nowrap">AI Concierge</span>
            <div className="w-10 h-10 rounded-full bg-[#F57C20] text-white flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
              <FiMessageSquare className="w-4 h-4" />
            </div>
          </button>
        </div>
      )}

      {/* Main toggle button */}
      <button
        onClick={() => setExpanded(!expanded)}
        data-testid="floating-toggle-btn"
        className={`flex items-center gap-2 rounded-full shadow-lg transition-all duration-300 ${
          expanded
            ? 'bg-[#1B2432] text-white shadow-black/20 w-14 h-14 justify-center'
            : 'bg-[#F57C20] text-white shadow-orange-500/30 hover:shadow-orange-500/50 pl-4 pr-3 py-3'
        }`}
      >
        {expanded ? (
          <FiX className="w-6 h-6" />
        ) : (
          <>
            <span className="text-xs font-semibold whitespace-nowrap">98200 88292</span>
            <ConnectIcon />
          </>
        )}
      </button>
    </div>
  );
};

export default FloatingButtons;
